import { InfoWithLabel } from "@/core/components/info-with-label";
import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  ConfidenceLabel,
  ManualJobMatchButton,
  PurchaseOrderField,
  AddedDate,
  ReviewSummary,
} from "@/accounts-payable/common";
import type { ApReceipt } from "@/accounts-payable/types";
import {
  canManuallyMatchJob,
  formatApDate,
  formatApDateTime,
  isNonJobExpense,
  jobMatchStrategyLabel,
  receiptPaymentLabel,
  stJobUrl,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";

export const ReceiptFieldsSection = ({
  receipt,
  businessId,
}: {
  receipt: ApReceipt;
  businessId: string | null;
}) => {
  const strategyLabel = jobMatchStrategyLabel(receipt.job_match_strategy);
  return (
    <Stack gap="md" fullWidth>
      <Stack direction="row" justify="between" align="end" gap="md" fullWidth>
        <InfoWithLabel label="Matched job" fullWidth={false}>
          {receipt.st_job_external_id ? (
            <Stack gap="xs">
              <Stack
                direction="row"
                gap="sm"
                align="center"
                className="flex-wrap"
              >
                <ServiceTitanIconLink
                  url={stJobUrl(receipt.st_job_external_id)}
                  label={`Open job #${receipt.st_job_external_id} in ServiceTitan`}
                  className="ml-0 text-lg text-interactive-text hover:text-interactive-text hover:underline"
                >
                  #{receipt.st_job_external_id}
                </ServiceTitanIconLink>
                {strategyLabel && (
                  <Typography variant="bodyW1" textColor="tertiary">
                    ({strategyLabel})
                  </Typography>
                )}
              </Stack>
              {receipt.job_match_confidence != null && (
                <ConfidenceLabel value={receipt.job_match_confidence} />
              )}
            </Stack>
          ) : isNonJobExpense(receipt) ? (
            <Typography variant="bodyW1">Non-job expense</Typography>
          ) : (
            <Typography variant="bodyW1" textColor="tertiary">
              Not matched
            </Typography>
          )}
        </InfoWithLabel>
        {canManuallyMatchJob(receipt) && (
          <ManualJobMatchButton
            businessId={businessId}
            documentId={receipt.id}
            documentType="receipt"
            agentRunId={receipt.agent_run_id}
            label={receipt.st_job_external_id ? "Change job" : undefined}
            variant={receipt.st_job_external_id ? "secondary" : "default"}
          />
        )}
      </Stack>
      <PurchaseOrderField document={receipt} businessId={businessId} />
      <Stack gap="none" fullWidth className="grid grid-cols-2 gap-4">
        <InfoWithLabel label="Purchased">
          {receipt.purchased_at
            ? formatApDateTime(receipt.purchased_at)
            : formatApDate(receipt.receipt_date)}
        </InfoWithLabel>
        <InfoWithLabel label="Added">
          <AddedDate value={receipt.created_at} withTime />
        </InfoWithLabel>
        <InfoWithLabel label="Review">
          <ReviewSummary document={receipt} />
        </InfoWithLabel>
        <InfoWithLabel label="Total">
          <Typography
            variant="bodyW1"
            textColor={receipt.total_amount_cents < 0 ? "negative" : "primary"}
          >
            {formatCentsOrDashIfZero(receipt.total_amount_cents)}
          </Typography>
        </InfoWithLabel>
        <InfoWithLabel label="PO / job name at the register">
          {receipt.po_number ?? "—"}
        </InfoWithLabel>
        <InfoWithLabel label="Paid with">
          {receiptPaymentLabel(receipt) ?? "—"}
        </InfoWithLabel>
        <InfoWithLabel label="Tax">
          {receipt.tax_amount_cents != null
            ? formatCentsOrDashIfZero(receipt.tax_amount_cents)
            : "—"}
        </InfoWithLabel>
        <InfoWithLabel label="Store">
          {[
            receipt.store_number && `#${receipt.store_number}`,
            receipt.store_address,
          ]
            .filter(Boolean)
            .join(" · ") || "—"}
        </InfoWithLabel>
      </Stack>
    </Stack>
  );
};
